import React from "react";
import { useTranslation } from "react-i18next";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Column, DataTable } from "@/components/ui/data-table";
import { CopyButton } from "@/components/common/CopyButton";
import { PlatformTenantAnalyticsRowDto } from "@/types/admin-analytics";
import { formatCents, formatCount } from "../utils/format";

interface TenantBreakdownTableProps {
  byTenant: PlatformTenantAnalyticsRowDto[];
  onExport: () => void;
  isExporting: boolean;
  canExport: boolean;
}

const shortenId = (id: string): string =>
  id.length > 12 ? `${id.slice(0, 8)}…` : id;

const IdCell = ({ id }: { id: string }): React.JSX.Element => (
  <span className="inline-flex items-center gap-1.5 text-sm">
    {shortenId(id)}
    <CopyButton value={id} />
  </span>
);

export const TenantBreakdownTable: React.FC<TenantBreakdownTableProps> = ({
  byTenant,
  onExport,
  isExporting,
  canExport,
}) => {
  const { t } = useTranslation();

  const columns: Column<PlatformTenantAnalyticsRowDto>[] = [
    {
      key: "tenantId",
      header: t("analytics.platform.table.tenant"),
      render: (_value, row) => <IdCell id={row.tenantId} />,
    },
    {
      key: "hotelId",
      header: t("analytics.platform.table.hotel"),
      render: (_value, row) => <IdCell id={row.hotelId} />,
    },
    {
      key: "region",
      header: t("analytics.platform.table.region"),
    },
    {
      key: "preCheckIn",
      header: t("analytics.platform.table.preCheckIn"),
      render: (_value, row) =>
        `${formatCount(row.preCheckInCompleted)} / ${formatCount(row.preCheckInStarted)}`,
    },
    {
      key: "preCheckOut",
      header: t("analytics.platform.table.preCheckOut"),
      render: (_value, row) =>
        `${formatCount(row.preCheckOutCompleted)} / ${formatCount(row.preCheckOutStarted)}`,
    },
    {
      key: "offers",
      header: t("analytics.platform.table.offers"),
      render: (_value, row) =>
        `${formatCount(row.offersPurchased)} / ${formatCount(row.offersOffered)}`,
    },
    {
      key: "offerRevenueCents",
      header: t("analytics.platform.table.revenue"),
      className: "text-right",
      render: (value: number) => (
        <span className="font-medium">{formatCents(value)}</span>
      ),
    },
    {
      key: "moduleUsageTotal",
      header: t("analytics.platform.table.moduleUsage"),
      className: "text-right",
      render: (value: number) => formatCount(value),
    },
    {
      key: "uniqueActiveGuests",
      header: t("analytics.platform.table.activeGuests"),
      className: "text-right",
      render: (value: number) => formatCount(value),
    },
  ];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base font-semibold">
          {t("analytics.platform.tenants.title")}
        </CardTitle>
        {canExport && (
          <Button
            variant="new_outline"
            onClick={onExport}
            disabled={isExporting || byTenant.length === 0}
          >
            <Download className="size-4" />
            {isExporting
              ? t("analytics.platform.exporting")
              : t("analytics.platform.exportCsv")}
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <DataTable
            data={byTenant}
            columns={columns}
            getRowKey={(row) => `${row.tenantId}-${row.hotelId}`}
            emptyMessage={t("analytics.platform.tenants.empty")}
          />
        </div>
      </CardContent>
    </Card>
  );
};
